
import Vue, { PropType } from "vue";
import { Box, BoxStatus, BoxStyle, DoorType, Sensor } from "@/models";

import BaseInputForm from "@/components/BaseInputForm.vue";
import BaseSelectorForm, {
  Option as SelectorOption,
} from "@/components/BaseSelectorForm.vue";
import BaseArrayCheckbox from "@/components/BaseArrayCheckbox.vue";
import BaseSubmitButton from "@/components/BaseSubmitButton.vue";
import BaseCancelButton from "@/components/BaseCancelButton.vue";
import BaseChuiGaki from "@/components/BaseChuiGaki.vue";

type Option = {
  name: string;
  value: string;
};

function convertBoxStatus(boxStatus?: string): BoxStatus {
  switch (boxStatus) {
    case "RUNNING":
      return BoxStatus.RUNNING;
    case "IN_MAINTENANCE":
      return BoxStatus.IN_MAINTENANCE;
    case "IN_PREPARING":
      return BoxStatus.IN_PREPARING;
    default:
      return BoxStatus.UNKNOWNBOXSTATUS;
  }
}

function convertBoxStyle(boxStyle?: string): BoxStyle {
  switch (boxStyle) {
    case "JAPANESE":
      return BoxStyle.JAPANESE;
    case "WESTERN":
      return BoxStyle.WESTERN;
    default:
      return BoxStyle.UNKNOWN_BOX_STYLE;
  }
}

function convertDoorType(doorType?: string): DoorType {
  switch (doorType) {
    case "NORMALLY_OPENED":
      return DoorType.NORMALLY_OPENED;
    case "NORMALLY_CLOSED":
      return DoorType.NORMALLY_CLOSED;
    default:
      return DoorType.UNKNOWN_DOOR_TYPE;
  }
}

export type DataType = {
  name: string;
  boxStyle: string;
  adaptedForWheelchair: boolean;
  doorType: string;
  primarySensorType: string;
  sensorMac: string;
  secondarySensorType: string;
  secondarySensorMac: string;
  sensorId: string;
  secondarySensorId: string;
  status: string;
  options: string[];
  isAdditionalRegisterButtonClicked: boolean;

  boxStyleOptions: Array<SelectorOption>;
  statusOptions: Array<SelectorOption>;
  optionOptions: Array<Option>;
  primarySensorTypeOptions: Array<SelectorOption>;
};

export default Vue.extend({
  name: "ToiletBoxEditor",

  components: {
    BaseChuiGaki,
    BaseInputForm,
    BaseSelectorForm,
    BaseArrayCheckbox,
    BaseSubmitButton,
    BaseCancelButton,
  },

  props: {
    box: Object as PropType<Box>,
    sensors: Array as PropType<Array<Sensor>>,
  },

  data(): DataType {
    return {
      name: "",
      boxStyle: "",
      adaptedForWheelchair: false,
      primarySensorType: "",
      doorType: "",
      sensorMac: "",
      secondarySensorType: "",
      secondarySensorMac: "",
      sensorId: "",
      secondarySensorId: "",
      status: "",
      options: [],
      isAdditionalRegisterButtonClicked: false,
      boxStyleOptions: [
        {
          name: "洋式",
          value: "WESTERN",
        },
        {
          name: "和式",
          value: "JAPANESE",
        },
      ],
      statusOptions: [
        {
          name: "メンテ中",
          value: "IN_MAINTENANCE",
        },
        {
          name: "稼動",
          value: "RUNNING",
        },
      ],
      optionOptions: [
        {
          name: "ベビーチェア",
          value: "BABYCHAIR",
        },
        {
          name: "おしり洗浄便座",
          value: "BIDET",
        },
        {
          name: "フィッティングボード",
          value: "FITTING_BOARD",
        },
        {
          name: "オストメイト",
          value: "OSTOMATE",
        },
        {
          name: "おむつ交換台",
          value: "DIAPER_CHANGING_TABLE",
        },
        {
          name: "車椅子利用",
          value: "WHEELCHAIR",
        },
        {
          name: "AirKnock®",
          value: "AIR_KNOCK",
        },
      ],
      primarySensorTypeOptions: [
        { name: "ドアセンサ-V1", value: "door-v1" },
        { name: "ドアセンサ-V2", value: "door-v2" },
        { name: "ドアセンサ-R1", value: "door-r1" },
        { name: "距離センサ", value: "distance" },
        { name: "新 MIC", value: "mic" },
        { name: "サーモセンサー", value: "thermopile" },
      ],
    };
  },

  computed: {
    canEditStatus(): boolean {
      return !!this.box?.id;
    },

    isEdited(): boolean {
      return (
        this.sensorMac !== "" ||
        this.box.name !== this.name ||
        this.box.boxStyle !== this.boxStyle ||
        this.box.status !== this.status ||
        this.box.options?.length !== this.options.length ||
        this.box.options.map((v) => this.options.includes(v)).includes(false)
      );
    },

    canSubmit(): boolean {
      return !!this.name && !!this.boxStyle && (!this.box || this.isEdited);
    },

    isNormallyOpened(): boolean {
      return this.doorType === "NORMALLY_OPENED";
    },

    isNormallyClosed(): boolean {
      return this.doorType === "NORMALLY_CLOSED";
    },

    switchSensors(): Sensor[] {
      return this.sensors.filter((s) => s.sensorType === "switch");
    },

    motionSensors(): Sensor[] {
      return this.sensors.filter((s) => s.sensorType === "motion");
    },

    sensorOptions(): Array<SelectorOption> {
      const sensorOptionsList = this.sensors.map(
        (s): SelectorOption => {
          return { name: s.mac || "", value: s.mac || "" };
        }
      );
      if (this.box?.sensorMac !== undefined) {
        sensorOptionsList.push({
          name: this.box?.sensorMac || "",
          value: this.box?.sensorMac || "",
        });
      }
      return sensorOptionsList;
    },
    isSensorComboMode(): boolean {
      return (
        this.isAdditionalRegisterButtonClicked &&
        (this.primarySensorType === "door-v1" ||
          this.primarySensorType === "door-r1")
      );
    },
    secondarySensorTypeOptions(): Array<SelectorOption> {
      if (this.primarySensorType === "door-v1") {
        return [{ name: "人感センサ-V1", value: "infrared-v1" }];
      } else {
        return [];
      }
    },
    primarySensorOptions(): Array<SelectorOption> {
      if (this.primarySensorType !== "") {
        return this.sensors
          .filter((sensor) => sensor.sensorType === this.primarySensorType)
          .map(
            (s): SelectorOption => {
              return { name: s.mac || "", value: s.mac || "" };
            }
          );
      } else {
        return []; //this.sensorOptions
      }
    },
    secondarySensorOptions(): Array<SelectorOption> {
      if (this.secondarySensorType !== "") {
        return this.sensors
          .filter((sensor) => sensor.sensorType === this.secondarySensorType)
          .map(
            (s): SelectorOption => {
              return { name: s.mac || "", value: s.mac || "" };
            }
          );
      } else {
        return [];
      }
    },
    canRegisterAdditionalSensor(): boolean {
      return (
        !this.isAdditionalRegisterButtonClicked &&
        (this.primarySensorType === "door-v1" ||
          this.primarySensorType === "door-r1")
      );
    },
  },

  watch: {
    box: function(n: Box) {
      this.name = n.name || "";
      this.boxStyle = n.boxStyle || "";
      this.doorType = n.doorType || "";
      this.sensorMac = n.sensorMac || "";
      this.options = n.options || [];
      this.status = n.status || "IN_PREPARING";
    },
    secondarySensorType: function() {
      this.secondarySensorMac = "";
    },
  },

  methods: {
    registerAdditionalSensor() {
      this.isAdditionalRegisterButtonClicked = true;
    },
    saveBox(): void {
      if (!this.canSubmit) {
        return;
      }
      const primarySensor = this.sensors.find(
        (sensor) => sensor.mac === this.sensorMac
      );
      const secondarySensor = this.sensors.find(
        (sensor) => sensor.mac === this.secondarySensorMac
      );
      const registeringSensorList = [];
      if (primarySensor !== undefined) {
        registeringSensorList.push(primarySensor);
      }
      if (secondarySensor !== undefined) {
        registeringSensorList.push(secondarySensor);
      }
      this.$emit(
        "save-box",
        new Box({
          name: this.name,
          boxStyle: convertBoxStyle(this.boxStyle),
          options: this.options,
          doorType: convertDoorType(this.doorType),
          sensorList: registeringSensorList,
          status: convertBoxStatus(this.status),
        })
      );
    },
  },
});
